const TRUSTED_HOST_SUFFIXES = ['myablefy.com', 'localhost'] as const

const isTrustedHost = (value: string): boolean => {
  if (!/^[a-z0-9.-]+(:\d{1,5})?$/i.test(value) || value.includes('..')) return false

  const hostname = value.split(':')[0].toLowerCase()
  return TRUSTED_HOST_SUFFIXES.some((suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`))
}

const isTrustedUrl = (value: string): boolean => {
  let url: URL

  try {
    url = new URL(value)
  } catch {
    return false
  }

  const isSecure = url.protocol === 'https:' || (url.protocol === 'http:' && url.hostname === 'localhost')

  return isSecure && value === url.origin && isTrustedHost(url.host)
}

const RUNTIME_ENVIRONMENTS = ['development', 'staging', 'uat', 'production'] as const

const isRuntimeEnvironment = (value: string): boolean => {
  const isKnownEnvironment = RUNTIME_ENVIRONMENTS.includes(value as (typeof RUNTIME_ENVIRONMENTS)[number])

  if (!isKnownEnvironment) return false

  return process.env.ENV === 'production' ? value === 'production' : value !== 'production'
}

const VALIDATORS = {
  NEXT_PUBLIC_API_CLIENT_URL: isTrustedUrl,
  NEXT_PUBLIC_WEB_HOST: isTrustedHost,
  NEXT_PUBLIC_RUNTIME_ENV: isRuntimeEnvironment,
} as const

const VALIDATION_MESSAGES = {
  NEXT_PUBLIC_API_CLIENT_URL: 'expected a trusted ablefy host or origin',
  NEXT_PUBLIC_WEB_HOST: 'expected a trusted ablefy host',
  NEXT_PUBLIC_RUNTIME_ENV: 'expected one of development, staging, uat, production',
} as const

export type RuntimeConfigKey = keyof typeof VALIDATORS

export type RuntimeConfigValues = Partial<Record<RuntimeConfigKey, string>>

const BUILD_TIME_CONFIG: RuntimeConfigValues = {
  NEXT_PUBLIC_API_CLIENT_URL: process.env.NEXT_PUBLIC_API_CLIENT_URL,
  NEXT_PUBLIC_WEB_HOST: process.env.NEXT_PUBLIC_WEB_HOST,
  NEXT_PUBLIC_RUNTIME_ENV: process.env.NEXT_PUBLIC_RUNTIME_ENV,
}

// Production uses build-time environment values and never needs the runtime file.
export const isRuntimeConfigEnabled = process.env.ENV !== 'production'

type GlobalWithRuntimeConfig = typeof globalThis & {
  __RUNTIME_CONFIG__?: RuntimeConfigValues
}

const RUNTIME_CONFIG_URL = '/runtime-config.json'

let fileConfig: RuntimeConfigValues | undefined

const readConfigFile = (): RuntimeConfigValues => {
  if (!isRuntimeConfigEnabled) return {}

  if (typeof window === 'undefined') {
    if (process.env.NEXT_PUBLIC_IS_NEXT_JS_APP !== 'true') return {}

    try {
      const nodeProcess = process as typeof process & {
        getBuiltinModule?: (moduleName: string) => { readFileSync: (path: string, encoding: string) => string }
      }
      const nodeFs = nodeProcess.getBuiltinModule?.('fs')
      if (!nodeFs) return {}

      const parsed: unknown = JSON.parse(nodeFs.readFileSync(`${process.cwd()}/public/runtime-config.json`, 'utf8'))
      return parsed && typeof parsed === 'object' ? (parsed as RuntimeConfigValues) : {}
    } catch {
      return {}
    }
  }

  if (fileConfig) return fileConfig

  fileConfig = {}

  // Shop receives the runtime values in the initial HTML from its custom
  // Next.js Document. If the bootstrap is missing, use the normal env/default
  // fallback below instead of making a second request from the browser.
  if (process.env.NEXT_PUBLIC_IS_NEXT_JS_APP === 'true') return fileConfig

  if (process.env.NODE_ENV === 'test') return fileConfig
  if (!isTrustedHost(window.location.host)) return fileConfig

  try {
    const request = new XMLHttpRequest()

    request.open('GET', RUNTIME_CONFIG_URL, false)
    request.send()

    if (request.status === 200) {
      const parsed: unknown = JSON.parse(request.responseText)
      if (parsed && typeof parsed === 'object') fileConfig = parsed as RuntimeConfigValues
    }
  } catch {
    return fileConfig
  }

  return fileConfig
}

export const readRuntimeConfig = (key: RuntimeConfigKey): string | undefined => {
  const injected = isRuntimeConfigEnabled
    ? (globalThis as GlobalWithRuntimeConfig).__RUNTIME_CONFIG__ ?? readConfigFile()
    : undefined
  const injectedValue = injected?.[key]
  const fromEnv = typeof process === 'undefined' ? undefined : process.env[key]

  if (injectedValue) {
    if (VALIDATORS[key](injectedValue)) return injectedValue

    console.warn(`runtime config: ignoring ${key}; ${VALIDATION_MESSAGES[key]}`)
  }

  const value = fromEnv || BUILD_TIME_CONFIG[key]

  if (!value) return undefined

  if (!VALIDATORS[key](value)) {
    console.warn(`runtime config: ignoring ${key}; ${VALIDATION_MESSAGES[key]}`)
    return undefined
  }

  return value
}

export const getRuntimeEnvironment = (): string | undefined => readRuntimeConfig('NEXT_PUBLIC_RUNTIME_ENV') || process.env.ENV

export const getRuntimeConfig = (): RuntimeConfigValues => {
  if (!isRuntimeConfigEnabled) return {}

  return {
    NEXT_PUBLIC_API_CLIENT_URL: readRuntimeConfig('NEXT_PUBLIC_API_CLIENT_URL'),
    NEXT_PUBLIC_WEB_HOST: readRuntimeConfig('NEXT_PUBLIC_WEB_HOST'),
    NEXT_PUBLIC_RUNTIME_ENV: readRuntimeConfig('NEXT_PUBLIC_RUNTIME_ENV'),
  }
}

export const serializeRuntimeConfig = (config: RuntimeConfigValues): string => {
  const serialized = JSON.stringify(config) || '{}'

  return serialized
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')
}
